import {yupResolver} from '@hookform/resolvers/yup';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import UndoIcon from '@mui/icons-material/Undo';
import {Button, Grid} from '@mui/material';
import React, {useState} from 'react';
import {FormProvider, useForm} from 'react-hook-form';
import {useSelector} from 'react-redux';
import {i18n} from 'src/i18n';
import authSelectors from 'src/modules/auth/authSelectors';
import yupFormSchemas from 'src/modules/shared/yup/yupFormSchemas';
import InputFormItem from 'src/view/shared/form/items/InputFormItem';
import TextAreaFormItem from 'src/view/shared/form/items/TextAreaFormItem';
import DivisionAutocompleteFormItem from 'src/view/division/autocomplete/DivisionAutocompleteFormItem';
import {
  applyDivisionIdPolicyToPayload,
  canShowDivisionIdFieldInForm,
  isTenantFixedAdminRole,
  mapRecordOrCurrentDivisionToFormDivisionId,
} from 'src/view/shared/division/divisionIdFormPolicy';
import FormWrapper, {FormButtons} from 'src/view/shared/styles/FormWrapper';
import * as yup from 'yup';

const schema = yup.object().shape({
  label: yupFormSchemas.string(i18n('entities.documentCategory.fields.label'), {
    required: true,
  }),
  description: yupFormSchemas.string(i18n('entities.documentCategory.fields.description'), {}),
  divisionId: yupFormSchemas.relationToOne(i18n('entities.division.label'), {required: false}),
});

function DocumentCategoryForm(props) {
  const tenantAccessContext = useSelector(authSelectors.selectTenantAccessContext);
  const roles = useSelector(authSelectors.selectRoles);

  const [initialValues] = useState(() => {
    const record = props.record || {};

    return {
      label: record.label,
      description: record.description,
      divisionId: mapRecordOrCurrentDivisionToFormDivisionId(record),
    };
  });

  const form = useForm({
    resolver: yupResolver(schema),
    mode: 'all',
    defaultValues: initialValues as any,
  });

  const showDivisionIdField = canShowDivisionIdFieldInForm(tenantAccessContext, isTenantFixedAdminRole(roles));

  const onSubmit = (values) => {
    const payload = {...values};
    applyDivisionIdPolicyToPayload(payload, tenantAccessContext, {
      treatAsTransversalSubmit: isTenantFixedAdminRole(roles),
    });
    props.onSubmit(props.record?.id, payload);
  };

  const onReset = () => {
    Object.keys(initialValues).forEach((key) => {
      form.setValue(key, initialValues[key]);
    });
  };

  const {saveLoading, modal} = props;

  return (
    <FormWrapper>
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <Grid spacing={2} container>
            <Grid item lg={6} md={8} sm={12} xs={12}>
              <InputFormItem name="label" label={i18n('entities.documentCategory.fields.label')} required={true} autoFocus />
            </Grid>
            {showDivisionIdField && !props.disabled && (
              <Grid item lg={6} md={8} sm={12} xs={12}>
                <DivisionAutocompleteFormItem
                  name="divisionId"
                  label={i18n('entities.division.label')}
                  required={false}
                  showCreate={false}
                />
              </Grid>
            )}
            <Grid item lg={6} md={8} sm={12} xs={12}>
              <TextAreaFormItem
                name="description"
                label={i18n('entities.documentCategory.fields.description')}
                required={false}
              />
            </Grid>
          </Grid>
          <FormButtons
            style={{
              flexDirection: modal ? 'row-reverse' : undefined,
            }}>
            <Button
              variant="contained"
              color="primary"
              disabled={saveLoading}
              type="button"
              onClick={form.handleSubmit(onSubmit)}
              startIcon={<SaveIcon />}
              size="small">
              {i18n('common.save')}
            </Button>

            <Button disabled={saveLoading} onClick={onReset} type="button" startIcon={<UndoIcon />} size="small">
              {i18n('common.reset')}
            </Button>

            {props.onCancel ? (
              <Button
                disabled={saveLoading}
                onClick={() => props.onCancel()}
                type="button"
                startIcon={<CloseIcon />}
                size="small">
                {i18n('common.cancel')}
              </Button>
            ) : null}
          </FormButtons>
        </form>
      </FormProvider>
    </FormWrapper>
  );
}

export default DocumentCategoryForm;
